/**
 * Dage, datoer, uger og klokkeslæt i STUDIETS tidszone (initData.timezone = tenant.timezone),
 * ikke i UTC og ikke i den besøgendes browser. Et hold kl. 00:30 dansk tid skal stå under sin
 * egen dag, også for en besøgende i New York eller en server i UTC (Tog 21-fund, 0f47cc3e7).
 *
 * Datoer uden klokkeslæt («dagnøgler», yyyy-mm-dd) er rene kalenderdatoer: de regnes og
 * formateres i UTC, så ingen tidszone kan flytte dem en dag.
 *
 * Bruges af ScheduleWidget, KlinikScheduleWidget og WeeklyScheduleWidget.
 */

export const STANDARD_TIDSZONE = 'Europe/Copenhagen'

/** Studiets tidszone; en manglende (ældre server) eller ugyldig værdi giver Europe/Copenhagen. */
export function studietsTidszone(tz?: string | null): string {
  if (!tz) return STANDARD_TIDSZONE
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz })
    return tz
  } catch {
    return STANDARD_TIDSZONE
  }
}

/** «18.00» / «6:00 PM» for et tidspunkt, i studiets tidszone. */
export function fmtTid(iso: string, locale: string, timeZone: string): string {
  return new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone })
}

/**
 * En dag/dato for et tidspunkt i en given tidszone, med kalderens egne felter
 * (fx `{ weekday: 'short', day: 'numeric', month: 'short' }`).
 */
export function fmtDag(iso: string, locale: string, timeZone: string, opts: Intl.DateTimeFormatOptions): string {
  return new Date(iso).toLocaleDateString(locale, { ...opts, timeZone })
}

/**
 * Rene datoer (@db.Date) og vægur (@db.Time, sendt som `1970-01-01THH:MM:00Z`) er ikke
 * tidspunkter: de vises i UTC, som alle andre steder i appen. I browserens zone viste en
 * dansk browser et forløbs-hold kl. 18:00 som 19:00 (København var UTC+1 i 1970).
 */
export const VAEGUR_TIDSZONE = 'UTC'

/**
 * «1.–4. juli 2026» / «28. december 2026 – 2. januar 2027» for et tidsrum (fx en retreat),
 * i studiets tidszone. Også «samme måned/år» afgøres dér, ikke i browserens zone.
 * Nogle sprog (da, de, nb …) slutter dag-formatet med punktum («23.»); det fjernes, før vi
 * sætter vores eget, ellers stod der «23..–25..».
 */
export function fmtDatoInterval(startIso: string, endIso: string, locale: string, timeZone: string): string {
  const start = new Date(startIso)
  const slut = new Date(endIso)
  const [sAar, sMaaned] = dagNoegle(start, timeZone).split('-')
  const [eAar, eMaaned] = dagNoegle(slut, timeZone).split('-')
  if (sAar === eAar && sMaaned === eMaaned) {
    // Samme dag (fx et event en aften): «1. december 2026», ikke «1.–1. december 2026».
    if (dagNoegle(start, timeZone) === dagNoegle(slut, timeZone)) {
      return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone }).format(start)
    }
    const dag = new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone })
    const d = (x: Date) => dag.format(x).replace(/\.$/, '')
    const maanedAar = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone })
    return `${d(start)}.–${d(slut)}. ${maanedAar.format(slut)}`
  }
  const startFmt = new Intl.DateTimeFormat(locale, {
    day: 'numeric', month: 'long', ...(sAar === eAar ? {} : { year: 'numeric' }), timeZone,
  })
  const slutFmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone })
  return `${startFmt.format(start)} – ${slutFmt.format(slut)}`
}

/** «01. december 2026» for en frist (fx retreatens depositum), i studiets tidszone. Tom ved null. */
export function fmtFrist(iso: string | null, locale: string, timeZone: string): string {
  if (!iso) return ''
  try {
    const bcp = locale === 'da' ? 'da-DK' : locale === 'en' ? 'en-GB' : locale
    return new Intl.DateTimeFormat(bcp, { day: '2-digit', month: 'long', year: 'numeric', timeZone }).format(new Date(iso))
  } catch {
    return iso
  }
}

/** «3. november» for et tidspunkt, i studiets tidszone. */
export function fmtDato(iso: string, locale: string, timeZone: string): string {
  return new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long', timeZone })
}

/** yyyy-mm-dd for et tidspunkt (eller nu), i studiets tidszone. */
export function dagNoegle(tidspunkt: string | Date, timeZone: string): string {
  const dele = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(typeof tidspunkt === 'string' ? new Date(tidspunkt) : tidspunkt)
  const del = (type: string) => dele.find(p => p.type === type)?.value ?? ''
  return `${del('year')}-${del('month')}-${del('day')}`
}

const middag = (noegle: string) => new Date(`${noegle}T12:00:00Z`)

/** 0 = søndag … 6 = lørdag for en dagnøgle. */
export function ugedagForNoegle(noegle: string): number {
  return middag(noegle).getUTCDay()
}

/** Dagnøglen `dage` dage efter (eller før) `noegle`. */
export function laegDageTil(noegle: string, dage: number): string {
  const d = middag(noegle)
  d.setUTCDate(d.getUTCDate() + dage)
  return d.toISOString().slice(0, 10)
}

/** Mandagen i samme uge som `noegle` (uger starter mandag). */
export function mandagFor(noegle: string): string {
  return laegDageTil(noegle, -((ugedagForNoegle(noegle) + 6) % 7))
}

/** Antal hele dage fra `fra` til `til` (dagnøgler). */
export function dageMellem(fra: string, til: string): number {
  return Math.round((middag(til).getTime() - middag(fra).getTime()) / 86_400_000)
}

/** En dagnøgle formateret som kalenderdato, fx { day: 'numeric', month: 'short' }. */
export function fmtNoegle(noegle: string, locale: string, opts: Intl.DateTimeFormatOptions): string {
  return middag(noegle).toLocaleDateString(locale, { ...opts, timeZone: 'UTC' })
}

/** ISO-8601-ugenummer for en dagnøgle (uger starter mandag; uge 1 har årets første torsdag). */
export function isoUgenummer(noegle: string): number {
  const torsdag = laegDageTil(mandagFor(noegle), 3)
  const aar = torsdag.slice(0, 4)
  return 1 + Math.floor(dageMellem(`${aar}-01-01`, torsdag) / 7)
}
